import { Injectable, inject, signal, effect } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { Client } from '@stomp/stompjs';
import { AuthService } from './auth.service';
import { TokenService } from './token.service';
import { NotificationService } from './notification.service';
import { NotificationDto } from '../../shared/models/messagerie.interface';

export interface IncomingCallEvent {
  callId: string;
  telephone: string;
  clientNom?: string;
  clientId?: string;
  filialeId?: string;
  timestamp: string;
}

export interface WebSocketNotification {
  id: string;
  titre: string;
  message: string;
  type: 'TRANSFERT' | 'MESSAGE' | 'DELAI' | 'SYSTEM';
  link?: string;
  lu: boolean;
  createdAt: string;
}

@Injectable({ providedIn: 'root' })
export class WebSocketService {
  private readonly authService = inject(AuthService, { optional: true });
  private readonly tokenService = inject(TokenService, { optional: true });
  private readonly notificationService = inject(NotificationService, { optional: true });

  readonly connectionStatus = signal<'connected' | 'disconnected' | 'reconnecting'>('connected');

  private stompClient: Client | null = null;
  private incomingCallSubject = new Subject<IncomingCallEvent | null>();
  private notificationSubject = new Subject<WebSocketNotification>();

  readonly incomingCall$: Observable<IncomingCallEvent | null> =
    this.incomingCallSubject.asObservable();
  readonly notifications$: Observable<WebSocketNotification> =
    this.notificationSubject.asObservable();

  private activeToken: string | null = null;
  private connecting = false;

  constructor() {
    // Watch current user and token to connect/disconnect
    if (this.authService && this.tokenService) {
      effect(() => {
        const user = this.authService?.currentUser();
        const token = this.tokenService?.getAccessToken();

        if (user && token) {
          if (this.activeToken !== token && !this.connecting) {
            this.activeToken = token;
            this.connecting = true;
            this.connect(token);
          }
        } else {
          this.activeToken = null;
          this.disconnect();
        }
      });
    }
  }

  private async connect(token: string): Promise<void> {
    // Désactiver proprement l'ancien client avant d'en créer un nouveau
    if (this.stompClient) {
      const old = this.stompClient;
      this.stompClient = null;
      try { await old.deactivate(); } catch { /* best-effort */ }
    }

    const protocol = typeof window !== 'undefined' && window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    let brokerURL = '';
    if (typeof window !== 'undefined') {
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        // En développement local, on se connecte directement à l'UAT puisque le proxy WebSocket n'est pas configuré.
        brokerURL = 'wss://crm-api-development.paas-local.dbafrica.net/crm-api/ws';
      } else {
        brokerURL = `${protocol}//${window.location.host}/crm-api/ws`;
      }
    }

    this.connectionStatus.set('reconnecting');

    this.stompClient = new Client({
      brokerURL,
      connectHeaders: {
        Authorization: `Bearer ${token}`,
      },
      // Pas de reconnexion automatique au niveau de la lib :
      // `reconnectDelay: 0` évite une boucle infinie de WebSocket lorsque la connexion échoue
      // (p. ex. token invalide). La reconnexion est pilotée par notre `effect()` (nouveau token).
      reconnectDelay: 0,
      heartbeatIncoming: 10000,
      heartbeatOutgoing: 10000,
      onConnect: () => {
        this.connecting = false;
        this.connectionStatus.set('connected');
        this.subscribeToNotifications();
      },
      onDisconnect: () => {
        this.connectionStatus.set('disconnected');
      },
      onWebSocketClose: () => {
        this.connectionStatus.set('disconnected');
      },
      onStompError: (frame) => {
        this.connecting = false;
        console.error('STOMP Error:', frame.headers['message'], frame.body);
        this.connectionStatus.set('disconnected');
      },
    });

    this.stompClient.activate();
  }

  private disconnect(): void {
    if (this.stompClient) {
      const old = this.stompClient;
      this.stompClient = null;
      void old.deactivate();
    }
    this.connecting = false;
    this.connectionStatus.set('disconnected');
  }

  private subscribeToNotifications(): void {
    if (!this.stompClient) return;

    this.stompClient.subscribe('/user/queue/notifications', (frame) => {
      try {
        const notifDto: NotificationDto = JSON.parse(frame.body);
        
        // Mettre à jour le service de notifications REST pour le badge dans la sidebar
        if (this.notificationService) {
          const currentList = this.notificationService.unreadNotifications();
          if (!currentList.some(n => n.id === notifDto.id)) {
            this.notificationService.unreadNotifications.set([notifDto, ...currentList]);
            this.notificationService.unreadCount.set(currentList.length + 1);
          }
        }

        // Diffuser la notification typée pour la cloche et le reste de l'application
        const mapped = this.mapNotification(notifDto);
        this.notificationSubject.next(mapped);
      } catch (err) {
        console.error('Error parsing notification frame body:', err);
      }
    });
  }

  /**
   * Permet aux composants de s'abonner dynamiquement à une destination précise.
   * Retourne la souscription STOMP pour pouvoir se désabonner plus tard.
   */
  subscribe<T = unknown>(destination: string, callback: (body: T) => void): { unsubscribe(): void } | null {
    if (!this.stompClient || !this.stompClient.connected) {
      console.warn('STOMP client not connected, subscription skipped for:', destination);
      return null;
    }
    return this.stompClient.subscribe(destination, (frame) => {
      try {
        const data = JSON.parse(frame.body);
        callback(data);
      } catch (err) {
        console.error(`Error parsing message on ${destination}:`, err);
      }
    });
  }

  private mapNotification(dto: NotificationDto): WebSocketNotification {
    let titre = 'Notification';
    let message = 'Nouvelle notification.';
    let type: 'TRANSFERT' | 'MESSAGE' | 'DELAI' | 'SYSTEM' = 'SYSTEM';
    let link = undefined;

    switch (dto.type) {
      case 'MESSAGE_RECU':
        titre = 'Nouveau message';
        message = 'Vous avez reçu un nouveau message.';
        type = 'MESSAGE';
        link = '/messagerie';
        break;
      case 'TICKET_TRANSFERE':
        titre = 'Ticket transféré';
        message = 'Un ticket vous a été transféré.';
        type = 'TRANSFERT';
        link = `/tickets/${dto.referenceId}`;
        break;
      case 'TICKET_ASSIGNE':
        titre = 'Ticket assigné';
        message = 'Un ticket vous a été assigné.';
        type = 'TRANSFERT';
        link = `/tickets/${dto.referenceId}`;
        break;
      case 'TICKET_RETOURNE':
        titre = 'Ticket retourné';
        message = 'Un ticket a été retourné.';
        type = 'TRANSFERT';
        link = `/tickets/${dto.referenceId}`;
        break;
    }

    return {
      id: dto.id,
      titre,
      message,
      type,
      link,
      lu: dto.lu ?? false,
      createdAt: dto.dateCreation || new Date().toISOString(),
    };
  }

  simulateIncomingCall(call: IncomingCallEvent): void {
    this.incomingCallSubject.next(call);
  }

  clearIncomingCall(): void {
    this.incomingCallSubject.next(null);
  }

  sendNotification(notification: WebSocketNotification): void {
    this.notificationSubject.next(notification);
  }
}
